import React from 'react';
import { useUIDSeed } from 'react-uid';

import { useFormik } from 'formik';
import { camelCase } from 'lodash';
import * as Yup from 'yup';

import { useSendBrokerInterestMutationResponse as ResponseData } from '@zego-types/useSendBrokerInterestMutation.graphql';
import CheckboxInput from '@zego/forms/CheckboxInput';
import { FormContext } from '@zego/forms/Form';
import FormActions from '@zego/forms/FormActions';
import FormField from '@zego/forms/FormField';
import Label from '@zego/forms/Label';
import SegmentedChoiceInput from '@zego/forms/SegmentedChoiceInput';
import useSendBrokerInterestMutation from '@zego/mutations/useSendBrokerInterestMutation';
import { BREAKPOINTS, unit } from '@zego/theme';
import { useTracking } from '@zego/tracking';

import Input from '@zego/components/atoms/Input';
import Spacer from '@zego/components/atoms/Spacer';
import Button from '@zego/components/button';

import { InitialFormValues, InterestFormProps } from '../interfaces';

const initialValues = (): InitialFormValues => ({
  brokerageName: '',
  firstNames: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  fleetsManaged: '',
  pricingRequest: false,
  taxiFleet: false,
  tpmFleet: false,
  businessFleet: false,
  microMobilityFleet: false,
  otherFleet: false,
});

// Also allows accented characters and whitespace for multiple given names
// See: https://stackoverflow.com/a/26900132
const textOnlyRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ ]+$/;
const phoneOnlyRegex = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const validationSchema = Yup.object().shape({
  brokerageName: Yup.string().required('Vereist'),
  firstNames: Yup.string()
    .matches(textOnlyRegex, 'Vul een geldige voornaam in alsjeblieft')
    .required('Vereist'),
  lastName: Yup.string()
    .matches(textOnlyRegex, 'Vul een geldige achternaam in alsjeblieft')
    .required('Vereist'),
  email: Yup.string()
    .email('Vul een geldig e-mailadres in alsjeblieft')
    .required('Vereist'),
  phoneNumber: Yup.string()
    .matches(phoneOnlyRegex, 'Vul hier je mobiele telefoonnummer in')
    .required('Vereist'),
  fleetsManaged: Yup.string().required('Vereist'),
  pricingRequest: Yup.boolean().required('Vereist'),
});

function Form(props) {
  const seed = useUIDSeed();

  return (
    <FormContext.Provider value={seed}>
      <form {...props} />
    </FormContext.Provider>
  );
}

const getCamelCase = (str: string): string => camelCase(str);

const BrokerInterestFormNL = ({
  onSuccess,
  id,
  country,
}: InterestFormProps): JSX.Element => {
  const { call: sendBrokerInterestMutation } = useSendBrokerInterestMutation(
    {},
  );

  const { trackEvent } = useTracking();

  const handleSubmit = (values, { setErrors, setSubmitting }) => {
    if (!values.worksWithFleets) values.worksWithFleets = true;
    if (!values.estimatedFleetGwp) values.estimatedFleetGwp = 0;
    if (!values.estimatedTotalGwp) values.estimatedTotalGwp = 0;
    if (!values.estimatedLeadsPerMonth) values.estimatedLeadsPerMonth = 0;
    values.country = country;

    trackEvent('Web: Contact form - form submited', {
      countryCode: country,
    });

    // pricingRequest starts as a real boolean but becomes a 'true'/'false'
    // radio string once selected; the schema declares it Boolean, so convert
    // a string selection and pass an untouched boolean through unchanged.
    const input = {
      ...values,
      pricingRequest:
        typeof values.pricingRequest === 'string'
          ? values.pricingRequest === 'true'
          : values.pricingRequest,
    };

    sendBrokerInterestMutation(
      {
        variables: { input },
      },
      (error, data: ResponseData) => {
        if (error) console.error(error);
        if (
          data?.sendBrokerInterest.__typename === 'SendBrokerInterestSuccess'
        ) {
          onSuccess(values.brokerageName);
        } else if (
          data.sendBrokerInterest.__typename === 'SendBrokerInterestError' &&
          data.sendBrokerInterest.fieldErrors
        ) {
          const { fieldErrors } = data.sendBrokerInterest;
          const errors = fieldErrors.reduce((acc, next) => {
            acc[getCamelCase(next.fieldName)] = next.errors;
            return acc;
          }, {});
          setErrors(errors);
          setSubmitting(false);
        }
      },
    );
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const styles = {
    input: {
      'Input:focus': {
        outline: 'none',
        border: '2px solid #46E18C',
      },
    },
    inputRadio: {
      span: {
        color: 'black',
        backgroundColor: 'white',
      },
      ':checked': {
        backgroundColor: 'white',
      },
    },
    inputCheckbox: {
      Input: {
        accentColor: '#46E18C',
      },
    },
    inputSubmit: {
      [BREAKPOINTS.medium]: {
        width: '143px',
        backgroundColor: '#46E18C',
        borderColor: '#46E18C',
        borderRadius: '40px',
        color: 'black',
      },
      ':hover': {
        backgroundColor: '#22D070',
        borderColor: '#22D070',
      },
    },
  };

  return (
    <Form onSubmit={formik.handleSubmit} id={id}>
      <Spacer height={unit(6)} />
      <FormField
        name="brokerageName"
        label="Bedrijfsnaam"
        placeholder="Vul bedrijfsnaam in"
        widget={Input}
        value={formik.values.brokerageName}
        error={formik.errors.brokerageName}
        onChange={event =>
          formik.setFieldValue('brokerageName', event.target.value, false)
        }
        css={styles.input}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="firstNames"
        label="Voornaam"
        placeholder="Vul voornaam in"
        widget={Input}
        value={formik.values.firstNames}
        error={formik.errors.firstNames}
        onChange={event =>
          formik.setFieldValue('firstNames', event.target.value, false)
        }
        css={styles.input}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="lastName"
        label="Achternaam"
        placeholder="Vul achternaam in"
        widget={Input}
        value={formik.values.lastName}
        error={formik.errors.lastName}
        onChange={event =>
          formik.setFieldValue('lastName', event.target.value, false)
        }
        css={styles.input}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="email"
        label="E-mail"
        placeholder="Vul e-mail in"
        widget={Input}
        value={formik.values.email}
        error={formik.errors.email}
        onChange={event =>
          formik.setFieldValue('email', event.target.value, false)
        }
        css={styles.input}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="phoneNumber"
        label="Telefoonnummer"
        placeholder="Vul telefoonnummer in"
        widget={Input}
        value={formik.values.phoneNumber}
        error={formik.errors.phoneNumber}
        onChange={event =>
          formik.setFieldValue('phoneNumber', event.target.value, false)
        }
        css={styles.input}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="fleetsManaged"
        label="Hoeveel wagenparken heb je momenteel in je portefeuille?"
        widget={SegmentedChoiceInput}
        widgetProps={{
          choices: [
            { label: '0 - 20', value: '0 - 20' },
            { label: '21 - 100', value: '21 - 100' },
            { label: '101+', value: '101+' },
          ],
        }}
        value={formik.values.fleetsManaged}
        error={formik.errors.fleetsManaged}
        onChange={event =>
          formik.setFieldValue('fleetsManaged', event.target.value, false)
        }
        css={styles.inputRadio}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="whoYouWorkWithLabel"
        label="Met wat voor soort wagenparken werk je?"
        widget={Label}
      />
      <FormField
        name="taxiFleet"
        widget={CheckboxInput}
        widgetProps={{
          label: 'Auto’s',
        }}
        value={formik.values.taxiFleet}
        onChange={event =>
          formik.setFieldValue(
            'taxiFleet',
            event.target.checked ? true : false,
            false,
          )
        }
        css={styles.inputCheckbox}
      />
      <FormField
        name="tpmFleet"
        widget={CheckboxInput}
        widgetProps={{
          label: 'Bedrijfsvoertuigen',
        }}
        value={formik.values.tpmFleet}
        onChange={event =>
          formik.setFieldValue(
            'tpmFleet',
            event.target.checked ? true : false,
            false,
          )
        }
        css={styles.inputCheckbox}
      />
      <FormField
        name="businessFleet"
        widget={CheckboxInput}
        widgetProps={{
          label: 'Bromfietsen',
        }}
        value={formik.values.businessFleet}
        onChange={event =>
          formik.setFieldValue(
            'businessFleet',
            event.target.checked ? true : false,
            false,
          )
        }
        css={styles.inputCheckbox}
      />
      <FormField
        name="microMobilityFleet"
        widget={CheckboxInput}
        widgetProps={{
          label: 'Micromobiliteit',
        }}
        value={formik.values.microMobilityFleet}
        onChange={event =>
          formik.setFieldValue(
            'microMobilityFleet',
            event.target.checked ? true : false,
            false,
          )
        }
        css={styles.inputCheckbox}
      />
      <FormField
        name="otherFleet"
        widget={CheckboxInput}
        widgetProps={{
          label: 'Anders',
        }}
        value={formik.values.otherFleet}
        onChange={event =>
          formik.setFieldValue(
            'otherFleet',
            event.target.checked ? true : false,
            false,
          )
        }
        css={styles.inputCheckbox}
      />
      <Spacer height={unit(3)} />
      <FormField
        name="pricingRequest"
        label="Moet je met spoed gecontacteerd worden?"
        widget={SegmentedChoiceInput}
        widgetProps={{
          choices: [
            { label: 'Ja', value: 'true' },
            { label: 'Nee', value: 'false' },
          ],
        }}
        value={formik.values.pricingRequest}
        error={formik.errors.pricingRequest}
        onChange={event =>
          formik.setFieldValue('pricingRequest', event.target.value, false)
        }
      />

      <FormActions css={{ display: 'block' }}>
        <Button
          type="submit"
          stretch={true}
          disabled={formik.isSubmitting}
          submitting={formik.isSubmitting}
          spinnerColor="#000"
          css={styles.inputSubmit}
        >
          Verzenden
        </Button>
      </FormActions>
    </Form>
  );
};

export default BrokerInterestFormNL;
